import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  buildReviewActivityItem,
  buildPayoutActivityItem,
  buildTransactionDetail,
  sumPaymentCents,
  sumDeliveredCents,
  computeNextPayout,
} from '@/features/wallet/utils/mappers';
import { walletRepository } from '@/features/wallet/repositories/supabaseWalletRepository';
import type { WalletSummary, WalletActivityItem, RollReviewTransaction } from '@/types/wallet';

export function useWalletSummary() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['wallet-summary', user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<WalletSummary> => {
      if (!user?.id) throw new Error('Not authenticated');

      const now          = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const startOfWeek  = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [released, incoming, monthReviews, weekReviews, { reviews, payouts }] =
        await Promise.all([
          walletRepository.fetchReleasedPayments(user.id),
          walletRepository.fetchIncomingPayments(user.id),
          walletRepository.fetchDeliveredReviews(user.id, startOfMonth),
          walletRepository.fetchDeliveredReviews(user.id, startOfWeek),
          walletRepository.fetchRecentActivity(user.id),
        ]);

      const reviewItems: WalletActivityItem[] = reviews.map(buildReviewActivityItem);
      const payoutItems: WalletActivityItem[] = payouts.map(buildPayoutActivityItem);
      const activity = [...reviewItems, ...payoutItems]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 25);

      const availableCents = sumPaymentCents(released);
      // Use the most recent payout's bank info as the destination for the next payout.
      // If there's no history, bank_type/bank_last4 will be null.
      const latestPayout = payouts[0] ?? null;

      return {
        available_balance_cents:  availableCents,
        incoming_cents:           sumPaymentCents(incoming),
        this_month_cents:         sumDeliveredCents(monthReviews),
        this_month_review_count:  monthReviews.length,
        this_week_cents:          sumDeliveredCents(weekReviews),
        this_week_review_count:   weekReviews.length,
        next_payout:              computeNextPayout(availableCents, latestPayout),
        activity,
      };
    },
  });
}

export function useRollReviewTransactionDetail(rollReviewId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['transaction-detail', rollReviewId],
    enabled: !!rollReviewId && !!user?.id,
    queryFn: async (): Promise<RollReviewTransaction | null> => {
      if (!rollReviewId || !user?.id) return null;
      const raw = await walletRepository.fetchTransactionDetail(rollReviewId, user.id);
      return raw ? buildTransactionDetail(raw) : null;
    },
  });
}
